'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faPhoneAlt,
  faEnvelope,
  faBars,
  faTimes,
  faChevronDown,
  faSignInAlt,
  faExternalLinkAlt,
  faFolder,
  faHome,
  faUser,
  faInfoCircle,
  faCalendarAlt,
  faUniversity,
  faGraduationCap,
  faFlask,
  faUsers,
  faHandshake,
  faAddressBook,
  faNewspaper,
  faBullhorn,
  faImages,
  faGlobe,
  faBook,
  faLaptop,
  faFileAlt,
  faDownload,
  faFilePdf
} from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { staticMenus, MenuGroup, MenuItem } from '@/config/menus';
import { fetchContentMenus, mapContentMenusToMenuGroups } from '@/libs/getMenus';
import { siteConfig } from '@/config/site';
import SearchOverlay from './SearchOverlay';
import LanguageSwitcher from '../ui/LanguageSwitcher';
import { useTranslation } from '@/context/LanguageContext';

import { useSettings } from '@/context/SettingsContext';
import { useUnit } from '@/context/UnitContext';

export default function Navbar() {
  const { t, language } = useTranslation();
  const { unitId, unitQueryParams } = useUnit();
  const { getSettingContent } = useSettings();
  const pathname = usePathname();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [dynamicMenus, setDynamicMenus] = useState<MenuGroup[]>([]);

  // Dynamic Settings
  const contactPhone = getSettingContent('social_phone', siteConfig.contact.phone);
  const contactEmail = getSettingContent('social_email', siteConfig.contact.email);

  // Icon Map
  const iconMap: Record<string, IconDefinition> = {
    'fa-home': faHome,
    'fa-user': faUser,
    'fa-info-circle': faInfoCircle,
    'fa-calendar-alt': faCalendarAlt,
    'fa-university': faUniversity,
    'fa-graduation-cap': faGraduationCap,
    'fa-flask': faFlask,
    'fa-users': faUsers,
    'fa-handshake': faHandshake,
    'fa-address-book': faAddressBook,
    'fa-newspaper': faNewspaper,
    'fa-bullhorn': faBullhorn,
    'fa-images': faImages,
    'fa-globe': faGlobe,
    'fa-book': faBook,
    'fa-laptop': faLaptop,
    'fa-file-alt': faFileAlt,
    'fa-download': faDownload,
    'fa-file-pdf': faFilePdf,
    'fa-folder': faFolder,
    'fa-external-link-alt': faExternalLinkAlt,
    'fa-search': faSearch,
    'fa-phone-alt': faPhoneAlt,
    'fa-envelope': faEnvelope,
    'fa-sign-in-alt': faSignInAlt,
  };

  const getIcon = (iconName?: string) => {
    if (!iconName) return null;
    // Handle both 'fa-home' and 'home' formats if needed, currently assuming 'fa-home'
    const icon = iconMap[iconName] || iconMap[`fa-${iconName}`];
    return icon;
  };

  const getTranslatedLabel = (id: string, defaultLabel: string) => {
    // Top level menus
    if (id === 'beranda') return t('nav.home');
    if (id === 'profil') return t('nav.profile');
    if (id === 'informasi') return t('nav.information');
    if (id === 'event') return t('nav.event');

    // Submenus - Profile
    if (id === 'tentang') return t('nav.about');
    if (id === 'akademik-info') return t('nav.academic');
    if (id === 'layanan') return t('nav.services');

    // Sub-submenus
    if (id === 'sejarah') return t('nav.history');
    if (id === 'struktur') return t('nav.structure');
    if (id === 'pimpinan') return t('nav.leadership');
    if (id === 'peta') return t('nav.campusMap');

    if (id === 'pendaftaran') return t('nav.admission');
    if (id === 'akademik') return t('nav.academicInfo');
    if (id === 'fakultas') return t('nav.faculties');
    if (id === 'riset') return t('nav.researchLink');

    if (id === 'kemahasiswaan') return t('nav.studentAffairs');
    if (id === 'kerjasama') return t('nav.cooperation');
    if (id === 'kontak') return t('nav.contact');

    // News/Info
    if (id === 'berita') return t('nav.news');
    if (id === 'pengumuman') return t('nav.announcement');
    if (id === 'buletin') return t('nav.bulletin');
    if (id === 'galeri') return t('nav.gallery');
    if (id === 'ppid') return t('nav.ppid');

    // Event submenus
    if (id === 'penelitian-pelatihan') return t('nav.researchTraining');
    if (id === 'seminar-webinar') return t('nav.seminarWebinar');
    if (id === 'lainnya') return t('nav.others');

    return defaultLabel;
  };

  // Fetch dynamic menus from API
  useEffect(() => {
    const loadDynamicMenus = async () => {
      if (!unitId && !unitQueryParams) return;

      try {
        const param = unitQueryParams || (unitId ? `unit_id=${unitId}` : '1');
        const contentMenus = await fetchContentMenus(param);
        const mapped = mapContentMenusToMenuGroups(contentMenus, language as 'id' | 'en');
        setDynamicMenus(mapped);
      } catch (error) {
        console.error('Failed to load dynamic menus:', error);
      }
    };
    loadDynamicMenus();
  }, [language, unitId, unitQueryParams]);

  const allMenus = [...staticMenus, ...dynamicMenus];

  // Helper function to check if a path is active
  const isPathActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleSearch = () => {
    setSearchOpen(!searchOpen);
    if (mobileMenuOpen) setMobileMenuOpen(false);
  };

  // Use ref for stable timeout management (prevents stale closure issues)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleDropdownEnter = useCallback((id: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setActiveDropdown(id);
  }, []);

  const handleDropdownLeave = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 250); // 250ms delay for smoother interaction
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Render a single menu item
  const renderMenuItem = (item: MenuItem, closeMenu?: () => void) => {
    const handleClick = () => {
      if (closeMenu) closeMenu();
    };

    const isActive = item.href && isPathActive(item.href);

    if (item.external) {
      return (
        <a
          key={item.id}
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 py-2 px-3 hover:bg-unmulPale rounded-lg transition text-sm text-gray-700 hover:text-unmulGreen"
          onClick={handleClick}
        >
          {item.icon && getIcon(item.icon) && <FontAwesomeIcon icon={getIcon(item.icon)!} className="w-4 text-unmulGreen" />}
          <span>{getTranslatedLabel(item.id, item.label)}</span>
          <FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs text-gray-400 ml-auto" />
        </a>
      );
    }

    return (
      <Link
        key={item.id}
        href={item.href || '#'}
        className={`flex items-center gap-2 py-2 px-3 rounded-lg transition text-sm relative ${isActive
          ? 'text-unmulGreen font-semibold'
          : 'text-gray-700 hover:bg-unmulPale hover:text-unmulGreen'
          }`}
        onClick={handleClick}
      >
        {isActive && <div className="absolute bottom-0 left-3 right-3 h-0.5 bg-unmulGreen"></div>}
        {item.icon && getIcon(item.icon) && <FontAwesomeIcon icon={getIcon(item.icon)!} className={`w-4 ${isActive ? 'text-unmulGreen' : 'text-unmulGreen'}`} />}
        <span>{getTranslatedLabel(item.id, item.label)}</span>
      </Link>
    );
  };

  // Render dropdown menu
  const renderDropdown = (menu: MenuGroup) => {
    // Check if any child item is active
    const hasActiveChild = menu.items?.some(section =>
      section.children?.some(child => child.href && isPathActive(child.href))
    );

    return (
      <div
        key={menu.id}
        className="relative group h-full flex items-center"
        onMouseEnter={() => handleDropdownEnter(menu.id)}
        onMouseLeave={handleDropdownLeave}
      >
        <button
          className={`flex items-center gap-1 px-4 py-2 transition text-sm font-medium relative z-10 rounded-lg ${hasActiveChild
            ? 'text-unmulGreen font-semibold'
            : activeDropdown === menu.id
              ? 'text-unmulGreen bg-unmulPale'
              : 'hover:text-unmulGreen'
            }`}
          onClick={() => handleDropdownEnter(menu.id)}
        >
          {getTranslatedLabel(menu.id, menu.label)}
          <FontAwesomeIcon icon={faChevronDown} className={`text-[10px] transition-transform ${activeDropdown === menu.id ? 'rotate-180' : ''}`} />
          {hasActiveChild && <div className="absolute bottom-0 left-4 right-4 h-0.5 bg-unmulGreen"></div>}
        </button>

        {/* Invisible bridge - Increased size and improved positioning */}
        <div className={`absolute top-1/2 left-0 w-full h-16 -translate-y-[10px] bg-transparent z-0 ${activeDropdown === menu.id ? 'block' : 'hidden'}`}></div>

        <div
          className={`absolute top-full left-0 mt-2 bg-white shadow-xl rounded-xl border border-gray-100 min-w-64 py-2 z-50 transition-all duration-200 origin-top ${activeDropdown === menu.id ? 'opacity-100 visible scale-100' : 'opacity-0 invisible scale-95 pointer-events-none'}`}
          onMouseEnter={() => handleDropdownEnter(menu.id)}
          onMouseLeave={handleDropdownLeave}
        >
          {menu.items?.map((section) => (
            <div key={section.id} className="px-2">
              {section.label && (
                <div className="text-xs font-bold text-unmulGreen uppercase tracking-wider px-3 py-2 border-b border-gray-100 mb-1">
                  {getTranslatedLabel(section.id, section.label)}
                </div>
              )}
              {section.children?.map((child) => renderMenuItem(child))}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render mega menu
  const renderMegaMenu = (menu: MenuGroup) => {
    const columns = menu.columns || 3;

    // Check if any child item is active
    const hasActiveChild = menu.items?.some(section =>
      section.children?.some(child =>
        (child.href && isPathActive(child.href)) ||
        child.children?.some(grandchild => grandchild.href && isPathActive(grandchild.href))
      )
    );

    // Render a menu item that might have nested children (3rd level)
    const renderNestedMenuItem = (item: MenuItem, closeMenu?: () => void) => {
      const handleClick = () => {
        if (closeMenu) closeMenu();
      };
      const isActive = item.href && isPathActive(item.href);

      // If item has children, render as a collapsible group
      if (item.children && item.children.length > 0) {
        return (
          <div key={item.id} className="mb-2">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-3 py-1 bg-gray-50 rounded-lg mb-1">
              {getTranslatedLabel(item.id, item.label)}
            </div>
            <div className="pl-2 space-y-0.5">
              {item.children.map((child) => {
                const isChildActive = child.href && isPathActive(child.href);
                return child.external ? (
                  <a
                    key={child.id}
                    href={child.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 py-1.5 px-3 hover:bg-unmulPale rounded-lg transition text-sm text-gray-600 hover:text-unmulGreen"
                    onClick={handleClick}
                  >
                    {child.icon && getIcon(child.icon) && <FontAwesomeIcon icon={getIcon(child.icon)!} className="w-4 text-unmulGreen text-xs" />}
                    <span className="text-xs">{getTranslatedLabel(child.id, child.label)}</span>
                    <FontAwesomeIcon icon={faExternalLinkAlt} className="text-[10px] text-gray-400 ml-auto" />
                  </a>
                ) : (
                  <Link
                    key={child.id}
                    href={child.href || '#'}
                    className={`flex items-center gap-2 py-1.5 px-3 rounded-lg transition text-xs ${isChildActive
                      ? 'text-unmulGreen font-semibold bg-unmulPale'
                      : 'text-gray-600 hover:bg-unmulPale hover:text-unmulGreen'
                      }`}
                    onClick={handleClick}
                  >
                    {child.icon && getIcon(child.icon) && <FontAwesomeIcon icon={getIcon(child.icon)!} className="w-4 text-unmulGreen" />}
                    <span>{getTranslatedLabel(child.id, child.label)}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        );
      }

      // Regular menu item without children
      if (item.external) {
        return (
          <a
            key={item.id}
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 py-2 px-3 hover:bg-unmulPale rounded-lg transition text-sm text-gray-700 hover:text-unmulGreen"
            onClick={handleClick}
          >
            {item.icon && getIcon(item.icon) && <FontAwesomeIcon icon={getIcon(item.icon)!} className="w-4 text-unmulGreen" />}
            <span>{getTranslatedLabel(item.id, item.label)}</span>
            <FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs text-gray-400 ml-auto" />
          </a>
        );
      }

      return (
        <Link
          key={item.id}
          href={item.href || '#'}
          className={`flex items-center gap-2 py-2 px-3 rounded-lg transition text-sm relative ${isActive
            ? 'text-unmulGreen font-semibold'
            : 'text-gray-700 hover:bg-unmulPale hover:text-unmulGreen'
            }`}
          onClick={handleClick}
        >
          {item.icon && getIcon(item.icon) && <FontAwesomeIcon icon={getIcon(item.icon)!} className="w-4 text-unmulGreen" />}
          <span>{getTranslatedLabel(item.id, item.label)}</span>
        </Link>
      );
    };

    return (
      <div
        key={menu.id}
        className="relative group h-full flex items-center"
        onMouseEnter={() => handleDropdownEnter(menu.id)}
        onMouseLeave={handleDropdownLeave}
      >
        <button
          className={`flex items-center gap-1 px-4 py-2 transition text-sm font-medium relative z-10 rounded-lg ${hasActiveChild
            ? 'text-unmulGreen font-semibold'
            : activeDropdown === menu.id
              ? 'text-unmulGreen bg-unmulPale'
              : 'hover:text-unmulGreen'
            }`}
          onClick={() => handleDropdownEnter(menu.id)}
        >
          {getTranslatedLabel(menu.id, menu.label)}
          <FontAwesomeIcon icon={faChevronDown} className={`text-[10px] transition-transform ${activeDropdown === menu.id ? 'rotate-180' : ''}`} />
          {hasActiveChild && <div className="absolute bottom-0 left-4 right-4 h-0.5 bg-unmulGreen"></div>}
        </button>

        {/* Invisible bridge - Extended to cover entire gap from button to menu */}
        <div
          className={`absolute top-full left-1/2 -translate-x-1/2 w-[200%] h-16 bg-transparent ${activeDropdown === menu.id ? 'block' : 'hidden'}`}
          onMouseEnter={() => handleDropdownEnter(menu.id)}
        ></div>

        <div
          className={`mega-menu-container fixed left-0 right-0 top-[100px] z-[60] transition-all duration-300 ${activeDropdown === menu.id ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2 pointer-events-none'}`}
          onMouseEnter={() => handleDropdownEnter(menu.id)}
          onMouseLeave={handleDropdownLeave}
        >
          <div className="container mx-auto px-6">
            <div className="bg-white shadow-2xl rounded-2xl border border-gray-100 p-8 mx-4 relative max-h-[80vh] overflow-y-auto">
              {/* Triangle pointer */}
              <div className="absolute -top-2 left-1/4 w-4 h-4 bg-white border-t border-l border-gray-100 transform rotate-45 z-50"></div>

              <div className={`grid grid-cols-${columns} gap-8`} style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
                {menu.items?.map((section) => (
                  <div key={section.id}>
                    <h5 className="text-unmulGreen font-bold mb-4 pb-2 border-b border-gray-200 flex items-center gap-2 text-sm uppercase tracking-wide">
                      <FontAwesomeIcon icon={faFolder} className="text-unmulYellow" />
                      {getTranslatedLabel(section.id, section.label)}
                    </h5>
                    <div className="space-y-1">
                      {section.children?.map((child) => renderNestedMenuItem(child))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render menu based on type
  const renderMenu = (menu: MenuGroup) => {
    const isActive = menu.href && isPathActive(menu.href);

    switch (menu.type) {
      case 'link':
        return (
          <Link
            key={menu.id}
            href={menu.href || '/'}
            className={`px-4 py-2 transition text-sm font-medium rounded-lg relative ${isActive
              ? 'text-unmulGreen font-semibold'
              : 'hover:text-unmulGreen hover:bg-gray-50'
              }`}
          >
            {getTranslatedLabel(menu.id, menu.label)}
            {isActive && <div className="absolute bottom-0 left-4 right-4 h-0.5 bg-unmulGreen"></div>}
          </Link>
        );
      case 'dropdown':
        return renderDropdown(menu);
      case 'megamenu':
        return renderMegaMenu(menu);
      default:
        return null;
    }
  };

  return (
    <>
      <nav className="fixed w-full z-50 transition-all duration-300 bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100">
        {/* Top Bar */}
        <div className="bg-unmulGreen text-white text-xs py-1.5 hidden lg:block">
          <div className="container mx-auto px-6 flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <a href={`tel:${contactPhone?.replace(/[^\d+]/g, '')}`} className="hover:text-unmulYellow transition flex items-center">
                <FontAwesomeIcon icon={faPhoneAlt} className="mr-1 text-unmulYellow" /> {contactPhone}
              </a>
              <a href={`mailto:${contactEmail}`} className="hover:text-unmulYellow transition flex items-center">
                <FontAwesomeIcon icon={faEnvelope} className="mr-1 text-unmulYellow" /> {contactEmail}
              </a>
            </div>
            <div className="flex items-center space-x-3">
              <a href="https://ais.unmul.ac.id" target="_blank" rel="noopener noreferrer" className="hover:text-unmulYellow transition">AIS</a>
              <span className="text-white">|</span>
              <a href="https://star.unmul.ac.id" target="_blank" rel="noopener noreferrer" className="hover:text-unmulYellow transition">E-Learning</a>
              <span className="text-white">|</span>
              <a href="https://sidak.unmul.ac.id" target="_blank" rel="noopener noreferrer" className="hover:text-unmulYellow transition">{t('nav.staffDosen')}</a>
              <span className="text-white">|</span>
              <LanguageSwitcher variant="compact" />
            </div>
          </div>
        </div>

        {/* Main Navigation Bar */}
        <div className="container mx-auto px-4 md:px-6 py-3 relative">
          <div className="flex justify-between items-center">
            {/* Logo Area */}
            <Link href="/" className="flex items-center space-x-3 group z-50 relative flex-shrink-0">
              <div className="relative w-10 h-10 md:w-12 md:h-12 flex-shrink-0">
                <Image
                  src={siteConfig.logo}
                  alt={`Logo ${siteConfig.name}`}
                  width={48}
                  height={48}
                  className="w-full h-full object-contain drop-shadow-md group-hover:scale-110 transition duration-300"
                />
              </div>
              <div className="flex flex-col max-w-[200px] md:max-w-[300px]">
                <span className="text-sm md:text-base lg:text-lg font-bold text-unmulGreen leading-tight font-serif tracking-wide line-clamp-2">
                  {(language === 'en' && siteConfig.nameEnglish ? siteConfig.nameEnglish : siteConfig.name).toUpperCase()}
                </span>
              </div>
            </Link>

            {/* Desktop Navigation - Dynamic Menus */}
            <div className="hidden xl:flex items-center text-gray-600">
              {allMenus.map((menu) => renderMenu(menu))}

              {/* Action Buttons */}
              <div className="pl-4 flex items-center gap-3 border-l border-gray-200 ml-4">
                <button
                  onClick={toggleSearch}
                  className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-unmulGreen transition"
                  aria-label="Pencarian"
                >
                  <FontAwesomeIcon icon={faSearch} />
                </button>
                <a
                  href="https://satu.unmul.ac.id"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-5 py-2.5 bg-unmulGreen text-white font-bold rounded-full hover:bg-unmulDarkGreen transition shadow-lg text-xs tracking-wider uppercase"
                >
                  Portal
                </a>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
              className="xl:hidden z-50 text-unmulGreen text-2xl p-2 hover:bg-green-50 rounded transition focus:outline-none"
            >
              <FontAwesomeIcon icon={mobileMenuOpen ? faTimes : faBars} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Backdrop - OUTSIDE nav for proper z-index */}
      <div
        className={`xl:hidden fixed inset-0 bg-black/50 z-[100] transition-opacity duration-300 ${mobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'}`}
        onClick={toggleMobileMenu}
      ></div>

      {/* Mobile Menu Drawer - OUTSIDE nav for proper z-index */}
      <div className={`xl:hidden fixed top-0 right-0 h-full w-[85%] max-w-sm bg-white z-[110] transition-transform duration-300 shadow-2xl ${mobileMenuOpen ? 'translate-x-0 overflow-y-auto' : 'translate-x-full invisible overflow-hidden'}`}>
        {/* Mobile Menu Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-unmulGreen text-white sticky top-0 z-10">
          <span className="font-bold text-lg">{t('nav.menu')}</span>
          <button
            onClick={toggleMobileMenu}
            aria-label="Close mobile menu"
            className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="px-4 py-4 space-y-2">
          {allMenus.map((menu) => {
            const isMenuActive = menu.href && isPathActive(menu.href);
            const hasActiveChild = menu.items?.some(section =>
              section.children?.some(child => child.href && isPathActive(child.href))
            );

            return (
              <div key={menu.id} className="border-b border-gray-100 pb-3">
                {menu.type === 'link' ? (
                  <Link
                    href={menu.href || '/'}
                    onClick={toggleMobileMenu}
                    className={`block py-3 px-2 text-base font-bold rounded-lg transition relative ${isMenuActive
                      ? 'text-unmulGreen'
                      : 'text-gray-800 hover:text-unmulGreen hover:bg-unmulPale'
                      }`}
                  >
                    {getTranslatedLabel(menu.id, menu.label)}
                    {isMenuActive && <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-unmulGreen"></div>}
                  </Link>
                ) : (
                  <details className="group">
                    <summary className={`flex items-center justify-between py-3 px-2 text-base font-bold cursor-pointer rounded-lg transition list-none [&::-webkit-details-marker]:hidden relative ${hasActiveChild
                      ? 'text-unmulGreen'
                      : 'text-unmulGreen hover:bg-unmulPale'
                      }`}>
                      {getTranslatedLabel(menu.id, menu.label)}
                      <FontAwesomeIcon icon={faChevronDown} className="text-xs transition-transform group-open:rotate-180" />
                      {hasActiveChild && <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-unmulGreen"></div>}
                    </summary>
                    <div className="mt-2 ml-2 pl-4 border-l-2 border-unmulGreen/20 space-y-1">
                      {menu.items?.map((section) => (
                        <div key={section.id}>
                          {section.label && (
                            <div className="text-xs font-bold text-gray-400 uppercase mt-3 mb-2 px-2">{getTranslatedLabel(section.id, section.label)}</div>
                          )}
                          {section.children?.map((child) => {
                            const isChildActive = child.href && isPathActive(child.href);
                            const hasGrandchildren = child.children && child.children.length > 0;

                            // If child has its own children (3rd level), render as nested details
                            if (hasGrandchildren) {
                              return (
                                <details key={child.id} className="group/nested">
                                  <summary className="flex items-center justify-between py-2 px-2 text-sm font-semibold text-gray-700 cursor-pointer rounded-lg hover:bg-unmulPale transition list-none [&::-webkit-details-marker]:hidden">
                                    <span>{getTranslatedLabel(child.id, child.label)}</span>
                                    <FontAwesomeIcon icon={faChevronDown} className="text-[10px] transition-transform group-open/nested:rotate-180 text-gray-400" />
                                  </summary>
                                  <div className="ml-2 pl-3 border-l border-gray-200 space-y-0.5 mt-1">
                                    {child.children?.map((grandchild) => {
                                      const isGrandchildActive = grandchild.href && isPathActive(grandchild.href);
                                      return grandchild.external ? (
                                        <a
                                          key={grandchild.id}
                                          href={grandchild.href}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          onClick={toggleMobileMenu}
                                          className="flex items-center gap-2 py-1.5 px-2 text-xs text-gray-600 hover:text-unmulGreen hover:bg-unmulPale rounded-lg transition"
                                        >
                                          <span>{getTranslatedLabel(grandchild.id, grandchild.label)}</span>
                                          <FontAwesomeIcon icon={faExternalLinkAlt} className="text-[10px] text-gray-400 ml-auto" />
                                        </a>
                                      ) : (
                                        <Link
                                          key={grandchild.id}
                                          href={grandchild.href || '#'}
                                          onClick={toggleMobileMenu}
                                          className={`flex items-center gap-2 py-1.5 px-2 text-xs rounded-lg transition ${isGrandchildActive
                                            ? 'text-unmulGreen font-semibold bg-unmulPale'
                                            : 'text-gray-600 hover:text-unmulGreen hover:bg-unmulPale'
                                            }`}
                                        >
                                          <span>{getTranslatedLabel(grandchild.id, grandchild.label)}</span>
                                        </Link>
                                      );
                                    })}
                                  </div>
                                </details>
                              );
                            }

                            // Regular child without grandchildren
                            return child.external ? (
                              <a
                                key={child.id}
                                href={child.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={toggleMobileMenu}
                                className="flex items-center gap-3 py-2 px-2 text-sm text-gray-600 hover:text-unmulGreen hover:bg-unmulPale rounded-lg transition"
                              >
                                {child.icon && getIcon(child.icon) && <FontAwesomeIcon icon={getIcon(child.icon)!} className="w-4 text-unmulGreen" />}
                                <span>{getTranslatedLabel(child.id, child.label)}</span>
                                <FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs text-gray-400 ml-auto" />
                              </a>
                            ) : (
                              <Link
                                key={child.id}
                                href={child.href || '#'}
                                onClick={toggleMobileMenu}
                                className={`flex items-center gap-3 py-2 px-2 text-sm rounded-lg transition relative ${isChildActive
                                  ? 'text-unmulGreen font-semibold'
                                  : 'text-gray-600 hover:text-unmulGreen hover:bg-unmulPale'
                                  }`}
                              >
                                {child.icon && getIcon(child.icon) && <FontAwesomeIcon icon={getIcon(child.icon)!} className="w-4 text-unmulGreen" />}
                                <span>{getTranslatedLabel(child.id, child.label)}</span>
                                {isChildActive && <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-unmulGreen"></div>}
                              </Link>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            );
          })}

          {/* Mobile Action Buttons */}
          <div className="pt-4 flex flex-col gap-3">
            <LanguageSwitcher variant="mobile" />
            <button
              onClick={toggleSearch}
              className="flex items-center justify-center w-full py-3 bg-gray-100 text-gray-700 rounded-lg font-bold hover:bg-gray-200 transition"
            >
              <FontAwesomeIcon icon={faSearch} className="mr-2" /> {t('nav.search')}
            </button>
            <a
              href="https://satu.unmul.ac.id"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-unmulGreen text-white text-center py-3 rounded-lg font-bold hover:bg-unmulDarkGreen transition"
            >
              <FontAwesomeIcon icon={faSignInAlt} className="mr-2" /> {t('nav.portal')}
            </a>
          </div>
        </div>
      </div>

      {/* Search Overlay */}
      <SearchOverlay isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
